import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const TYPES = [
  ["control", "Control"], ["vendor", "Vendor"], ["incident", "Incident"],
  ["request", "Request"], ["retention_batch", "Retention Batch"],
];

export const LinkEditor = ({ links, onChange }) => {
  const [type, setType] = useState("control");
  const [entityId, setEntityId] = useState("");
  const [label, setLabel] = useState("");
  const [q, setQ] = useState("");
  const [controls, setControls] = useState([]);

  useEffect(() => {
    if (type !== "control") return;
    const t = setTimeout(() => {
      api.get("/platform/link-targets", { params: { type: "control", q: q || undefined } })
        .then(({ data }) => setControls(data.items)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [type, q]);

  const add = () => {
    if (!entityId) return;
    const ctrl = controls.find((c) => c.id === entityId);
    onChange([...links, { entity_type: type, entity_id: entityId, label: label || ctrl?.label || entityId }]);
    setEntityId(""); setLabel(""); setQ("");
  };

  return (
    <div className="space-y-2" data-testid="task-link-editor">
      <div className="flex flex-wrap gap-1.5">
        {links.map((l, i) => (
          <span key={`${l.entity_type}-${l.entity_id}`} data-testid={`task-link-chip-${i}`}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-slate-100 text-[11px] text-slate-700">
            <span className="font-semibold uppercase text-[10px] text-slate-500">{l.entity_type.replace("_", " ")}</span>
            {l.label || l.entity_id}
            <button type="button" onClick={() => onChange(links.filter((_, j) => j !== i))}
                    className="w-4 h-4 rounded-full hover:bg-slate-200 flex items-center justify-center"
                    data-testid={`task-link-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {!links.length && <span className="text-xs text-slate-400">No links yet.</span>}
      </div>
      <div className="grid grid-cols-[140px_1fr_auto] gap-2">
        <Select value={type} onValueChange={(v) => { setType(v); setEntityId(""); }}>
          <SelectTrigger className="h-9 text-xs" data-testid="task-link-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {type === "control" ? (
          <div className="flex gap-2 min-w-0">
            <Input className="h-9 text-xs w-24" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="task-link-control-search" />
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger className="h-9 text-xs min-w-0" data-testid="task-link-control-select">
                <SelectValue placeholder="Pick control" />
              </SelectTrigger>
              <SelectContent>
                {controls.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}{c.context ? ` — ${c.context}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex gap-2 min-w-0">
            <Input className="h-9 text-xs" placeholder="Reference ID" value={entityId}
                   onChange={(e) => setEntityId(e.target.value)} data-testid="task-link-entity-id" />
            <Input className="h-9 text-xs" placeholder="Label (optional)" value={label}
                   onChange={(e) => setLabel(e.target.value)} data-testid="task-link-label" />
          </div>
        )}
        <Button type="button" size="sm" variant="outline" className="h-9" onClick={add} disabled={!entityId}
                data-testid="task-link-add">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
